// Users management page (admin only)
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PencilIcon, XMarkIcon, CheckIcon, PlusIcon, UserPlusIcon } from '@heroicons/react/20/solid'
import { apiRequest } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { Avatar } from '../../components/ui/Avatar'

interface UserListItem {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: string
  managerId: string | null
  createdAt: string
}

interface RoleOption {
  value: string
  label: string
}

interface NewUserForm {
  email: string
  firstName: string
  lastName: string
  password: string
  role: string
  managerId: string | null
}

const emptyNewUser: NewUserForm = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  role: 'sales_rep',
  managerId: null,
}

function getUserName(user: UserListItem): string {
  const parts = [user.firstName, user.lastName].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : user.email
}

// Helper to get translated role label
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRoleLabel(role: string, t: (key: any) => string): string {
  const key = `roles.${role}`
  const translated = t(key)
  // If no translation found (returns the key), fall back to formatted role name
  if (translated === key) {
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }
  return translated
}

export function UsersListPage() {
  const { t } = useTranslation()
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<{ role: string; managerId: string | null }>({ role: '', managerId: null })
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [showNewUserForm, setShowNewUserForm] = useState(false)
  const [newUser, setNewUser] = useState<NewUserForm>(emptyNewUser)
  const [createError, setCreateError] = useState<string | null>(null)

  // Fetch all users
  const { data: usersResponse, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiRequest<{ data: UserListItem[] }>('/users'),
  })

  // Fetch available roles
  const { data: rolesResponse } = useQuery({
    queryKey: ['users', 'roles'],
    queryFn: () => apiRequest<{ data: RoleOption[] }>('/users/roles'),
  })

  // Update user mutation
  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { role?: string; managerId?: string | null } }) =>
      apiRequest(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
  })

  // Create user mutation
  const createUser = useMutation({
    mutationFn: (data: NewUserForm) =>
      apiRequest('/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowNewUserForm(false)
      setNewUser(emptyNewUser)
      setCreateError(null)
    },
    onError: (error: Error) => {
      setCreateError(error.message || t('common.error'))
    },
  })

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError(null)
    createUser.mutate(newUser)
  }

  const allUsers = usersResponse?.data || []
  const roles = rolesResponse?.data || []

  // Filter users by role
  const users = roleFilter === 'all'
    ? allUsers
    : allUsers.filter(u => u.role === roleFilter)

  // Get potential managers (users with sales_manager or admin role)
  const potentialManagers = allUsers.filter(u => u.role === 'admin' || u.role === 'sales_manager')

  const startEdit = (user: UserListItem) => {
    setEditingUser(user.id)
    setEditValues({ role: user.role, managerId: user.managerId })
  }

  const cancelEdit = () => {
    setEditingUser(null)
    setEditValues({ role: '', managerId: null })
  }

  const saveEdit = (userId: string) => {
    updateUser.mutate({
      id: userId,
      data: {
        role: editValues.role,
        managerId: editValues.managerId,
      },
    })
  }

  // Check if current user can manage users
  const canManageUsers = currentUser?.role === 'admin'

  // Count users by role
  const roleCounts = allUsers.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  if (!canManageUsers) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('users.title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('users.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-gray-900">{t('common.accessDenied')}</h2>
              <p className="mt-2 text-sm text-gray-500">{t('users.adminOnly')}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (usersLoading) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('users.title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('users.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 flex items-center justify-center">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        </div>
      </>
    )
  }

  if (usersError) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('users.title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('users.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-red-600">{t('common.error')}</h2>
              <p className="mt-2 text-sm text-gray-500">{(usersError as Error).message}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Secondary column - Filters and stats */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('users.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('users.subtitle')}</p>
          </div>

          <button
            type="button"
            onClick={() => setShowNewUserForm(!showNewUserForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <UserPlusIcon className="h-5 w-5" />
            {showNewUserForm ? t('common.cancel') : t('users.addUser')}
          </button>

          {showNewUserForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('users.newUser')}</h3>
              {createError && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {createError}
                </div>
              )}
              <form onSubmit={handleCreateUser} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">{t('fields.firstName')}</label>
                    <input
                      type="text"
                      value={newUser.firstName}
                      onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">{t('fields.lastName')}</label>
                    <input
                      type="text"
                      value={newUser.lastName}
                      onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {t('fields.email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {t('users.password')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">{t('fields.role')}</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {getRoleLabel(role.value, t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">{t('fields.manager')}</label>
                  <select
                    value={newUser.managerId || ''}
                    onChange={(e) => setNewUser(prev => ({ ...prev, managerId: e.target.value || null }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    <option value="">{t('users.noManager')}</option>
                    {potentialManagers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {getUserName(m)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewUserForm(false)
                      setNewUser(emptyNewUser)
                      setCreateError(null)
                    }}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createUser.isPending}
                    className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {createUser.isPending ? t('common.saving') : t('common.create')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Role Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.role')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setRoleFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  roleFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')} ({allUsers.length})
              </button>
              {roles.map(role => (
                <button
                  key={role.value}
                  onClick={() => setRoleFilter(role.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    roleFilter === role.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getRoleLabel(role.value, t)} ({roleCounts[role.value] || 0})
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.leads.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.leads.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{allUsers.length}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('users.managers')}</dt>
                <dd className="font-medium text-gray-900">{potentialManagers.length}</dd>
              </div>
              {roleFilter !== 'all' && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">{t('common.filtered')}</dt>
                  <dd className="font-medium text-gray-900">{users.length}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Team Hierarchy Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('users.hierarchy')}</h3>
            <p className="text-xs text-gray-500">
              {t('users.hierarchyDescription')}
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('users.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('users.subtitle')}</p>

            {/* Mobile role filter */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setRoleFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  roleFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {roles.map(role => (
                <button
                  key={role.value}
                  onClick={() => setRoleFilter(role.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    roleFilter === role.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getRoleLabel(role.value, t)}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t('fields.user')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.email')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.role')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.manager')}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {users.map((user) => {
                        const isEditing = editingUser === user.id
                        const manager = user.managerId ? allUsers.find(u => u.id === user.managerId) : null
                        const isCurrentUser = user.id === currentUser?.id

                        return (
                          <tr key={user.id} className={`${isCurrentUser ? 'bg-brand-50' : ''} hover:bg-gray-50 transition-colors`}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <div className="flex items-center gap-3">
                                <Avatar firstName={user.firstName || user.email} lastName={user.lastName} size="sm" />
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {getUserName(user)}
                                    {isCurrentUser && (
                                      <span className="ml-2 inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                                        {t('users.you')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {user.email}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {isEditing ? (
                                <select
                                  value={editValues.role}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, role: e.target.value }))}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                >
                                  {roles.map((role) => (
                                    <option key={role.value} value={role.value}>
                                      {getRoleLabel(role.value, t)}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                                  {getRoleLabel(user.role, t)}
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {isEditing ? (
                                <select
                                  value={editValues.managerId || ''}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, managerId: e.target.value || null }))}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                >
                                  <option value="">{t('users.noManager')}</option>
                                  {potentialManagers
                                    .filter(m => m.id !== user.id) // Can't be your own manager
                                    .map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {getUserName(m)}
                                      </option>
                                    ))}
                                </select>
                              ) : manager ? (
                                <div className="flex items-center gap-2">
                                  <Avatar firstName={manager.firstName || manager.email} lastName={manager.lastName} size="xs" />
                                  <span>{getUserName(manager)}</span>
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              {isEditing ? (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => saveEdit(user.id)}
                                    disabled={updateUser.isPending}
                                    className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-900 disabled:opacity-50"
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                    {updateUser.isPending ? t('common.saving') : t('common.save')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                    {t('common.cancel')}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => startEdit(user)}
                                  className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-900"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                  {t('common.edit')}
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  {users.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">{t('users.noUsers')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
